import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, ListItem, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { SystemRolesName } from 'src/api';
import { State } from 'src/types';
import {
  GetRulesByStatusQuery,
  useApproveRetentionRuleMutation,
  useDeclineRetentionRuleMutation,
} from '../../../../graphql/generated';
import { ExpandableCard } from '../../molecules/ExpandableCard';
import { LensButton } from '../../molecules/LensButton';
import { RuleFieldsList } from '../../molecules/RuleFieldsList';
import {
  CaseCategory,
  getConditionLabel,
  getConditionValue,
  RelationshipType,
  useContentPadding,
} from '../../utils';

type RetentionRule = NonNullable<
  GetRulesByStatusQuery['getRulesByStatus']
>[number];

interface RuleCardProps {
  rule: RetentionRule;
  relationshipTypes: RelationshipType[];
  caseCategories: CaseCategory[];
  isExpanded: boolean;
  onToggle: (id: string) => void;
}

export const RuleCard = ({
  rule,
  relationshipTypes,
  caseCategories,
  isExpanded,
  onToggle,
}: RuleCardProps) => {
  const { tokens } = useTheme<LensTheme>();
  const contentPadding = useContentPadding();
  const queryClient = useQueryClient();
  const currentUser = useSelector(
    (state: State) => state.globalUsers.currentUser,
  );

  const isSuperAdmin = currentUser?.SystemRoles?.some(
    (role) => role.name === SystemRolesName.SUPER,
  );
  const canActOnRule =
    isSuperAdmin && Number(currentUser?.id) !== rule.authorId;

  const { mutate: approveRule } = useApproveRetentionRuleMutation({
    onSuccess: () => {
      queryClient.invalidateQueries('GetRulesByStatus');
      queryClient.invalidateQueries('GetRetentionRulesCount');
      queryClient.invalidateQueries('GetUpcomingRetentionActionsCount');
    },
    onError: (error) => console.error('Failed to approve rule:', error),
  });

  const { mutate: declineRule } = useDeclineRetentionRuleMutation({
    onSuccess: () => {
      queryClient.invalidateQueries('GetRulesByStatus');
      queryClient.invalidateQueries('GetRetentionRulesCount');
      queryClient.invalidateQueries('GetUpcomingRetentionActionsCount');
    },
    onError: (error) => console.error('Failed to decline rule:', error),
  });

  const conditions = rule.conditions;
  const conditionLabel = getConditionLabel(conditions?.condition);
  const conditionValue = getConditionValue(
    conditions,
    relationshipTypes,
    caseCategories,
  );

  const fieldData = [
    { label: 'Country', value: rule.country },
    { label: 'Rule type', value: rule.ruleType },
    { label: 'Created', value: dayjs(rule.createdAt).format('D/M/YYYY') },
    { label: 'Last updated', value: dayjs(rule.updatedAt).format('D/M/YYYY') },
    {
      label: 'Conditions',
      value: conditions?.applyToAll ? (
        'Applies to all cases'
      ) : conditions?.condition && conditions?.conditionValue ? (
        <>
          <span
            style={{ fontWeight: tokens.semantic.fontWeight.emphasis.value }}
          >
            {conditionLabel}
          </span>
          {` = ${conditionValue}`}
        </>
      ) : (
        '-'
      ),
    },
  ];

  const hasCountry = !!rule.country;
  const label = hasCountry
    ? `${rule.ruleType} · ${rule.country}`
    : conditionLabel || 'Fallback Rule';
  const subtitle = hasCountry ? conditionLabel || '-' : undefined;

  const isImmediateRule = rule.dateReference === 'IMMEDIATE';
  const isDeletionRule = rule.ruleType === 'DELETION';

  return (
    <ExpandableCard
      key={rule.id}
      title={label}
      subtitle={subtitle}
      toggleKey={rule.id}
      items={[rule]}
      isExpanded={isExpanded}
      onToggle={onToggle}
      chipLabel={
        isImmediateRule
          ? 'Immediate Action'
          : isDeletionRule
            ? 'Deletion'
            : 'Manual Review'
      }
      variant="actions"
      renderExpandedContent={() => (
        <ListItem
          sx={{
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: tokens.core.spacing[2].value,
            width: '100%',
            boxShadow: 'none',
          }}
        >
          <Box
            sx={{
              padding: contentPadding,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: tokens.core.spacing['1_5'].value,
              width: '100%',
              boxShadow: 'none',
            }}
          >
            <span
              style={{ fontWeight: tokens.semantic.fontWeight.emphasis.value }}
            >
              Rule configuration
            </span>
          </Box>
          <Box
            sx={{
              padding: contentPadding,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: tokens.core.spacing[0].value,
              width: '100%',
              boxShadow: 'none',
            }}
          >
            <RuleFieldsList fields={fieldData} variant="emphasis" />
            <RuleFieldsList fields={fieldData} variant="body" />
          </Box>
          <Box
            sx={{
              padding: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: tokens.core.spacing[2].value,
              width: '100%',
              boxShadow: 'none',
            }}
          >
            <LensButton
              variant="secondary"
              buttonSize="small"
              disabled={!canActOnRule}
              onClick={() => declineRule({ input: { id: rule.id } })}
            >
              Decline
            </LensButton>
            <LensButton
              variant="primary"
              buttonSize="small"
              disabled={!canActOnRule}
              onClick={() => approveRule({ id: rule.id })}
            >
              Approve
            </LensButton>
          </Box>
        </ListItem>
      )}
    />
  );
};
